import { useRef, useState } from "react";
import { Download, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const Line = ({ label, s, testid }) => (
  <div className="rounded-md border border-slate-200 p-3 text-sm" data-testid={testid}>
    <p className="font-medium text-slate-800">{label}</p>
    <p className="mt-1 text-slate-600"><span className="text-emerald-700">+{s.create} baru</span> &middot; <span className="text-sky-700">{s.update} diubah</span> &middot; <span className={s.delete ? "font-semibold text-red-600" : ""}>{s.delete} dihapus</span></p>
  </div>
);

export const SeatBulk = ({ onDone }) => {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    try {
      const res = await api.get("/seat/export", { responseType: "blob" });
      const name = res.headers["content-disposition"]?.match(/filename="(.+)"/)?.[1] || "status-seat.xlsx";
      const url = URL.createObjectURL(res.data);
      Object.assign(document.createElement("a"), { href: url, download: name }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const send = async (f, apply) => {
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post(`/seat/import?apply=${apply}`, fd);
      if (apply) { toast.success("Upload berhasil, data diperbarui"); setResult(null); onDone(); } else setResult(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) { setFile(f); send(f, false); }
  };

  return (
    <>
      <Button variant="outline" onClick={download} className="rounded-full" data-testid="seat-download-button"><Download className="h-4 w-4 mr-1" /> Download Excel</Button>
      <Button variant="outline" onClick={() => input.current.click()} disabled={busy} className="rounded-full" data-testid="seat-upload-button"><Upload className="h-4 w-4 mr-1" /> {busy && !result ? "Membaca..." : "Upload Excel"}</Button>
      <input ref={input} type="file" accept=".xlsx" className="hidden" onChange={pick} data-testid="seat-upload-input" />
      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="seat-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{result?.ok ? "Periksa perubahan" : "File belum bisa diproses"}</DialogTitle>
            <DialogDescription>{file?.name}</DialogDescription>
          </DialogHeader>
          {result?.ok ? (
            <div className="space-y-3">
              <Line label="Produk" s={result.products} testid="seat-upload-summary-products" />
              <Line label="Tanggal keberangkatan" s={result.departures} testid="seat-upload-summary-departures" />
              {result.stale && (
                <p className="flex gap-2 rounded-md bg-amber-50 p-3 text-xs text-amber-800" data-testid="seat-upload-stale"><AlertTriangle className="h-4 w-4 shrink-0" />Ada perubahan di aplikasi setelah file ini didownload. Jika diterapkan, perubahan tersebut akan tertimpa atau terhapus. Sebaiknya download ulang file terbaru.</p>
              )}
              {(result.products.delete > 0 || result.departures.delete > 0) && (
                <p className="flex gap-2 rounded-md bg-red-50 p-3 text-xs text-red-700"><AlertTriangle className="h-4 w-4 shrink-0" />{result.products.delete} dari {result.total_products} produk dan {result.departures.delete} dari {result.total_departures} tanggal tidak ada di file dan akan dihapus permanen dari aplikasi.</p>
              )}
            </div>
          ) : (
            <ul className="max-h-72 space-y-1 overflow-y-auto rounded-md bg-red-50 p-3 text-xs text-red-700" data-testid="seat-upload-errors">
              {result?.errors.map((e, i) => <li key={i}>{e}</li>)}
              {result?.error_count > result?.errors.length && <li>...dan {result.error_count - result.errors.length} kesalahan lain</li>}
            </ul>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setResult(null)}>{result?.ok ? "Batal" : "Tutup"}</Button>
            {result?.ok && <Button disabled={busy} onClick={() => send(file, true)} className="rounded-full bg-brand hover:bg-brand-dark text-white" data-testid="seat-upload-confirm">{busy ? "Menyimpan..." : "Terapkan perubahan"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
